import React, { useState } from 'react'
import {
  ArrowTopRightOnSquareIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline'

import { Button, Input, Label, Spinner } from '../../../../components/ui'
import { useToasts } from '../../../../stores/useToasts'
import type { SheetsSyncMutation } from '../../domain/contractWorkflow'
import { getErrorMessage } from '../../domain/formatters'
import type { ContractContext } from '../../types'

export function GoogleSheetsSyncPanel({
  context,
  syncSheets,
}: {
  context?: ContractContext
  syncSheets: SheetsSyncMutation
}) {
  const pushToast = useToasts(state => state.push)
  const [spreadsheetId, setSpreadsheetId] = useState('')
  const [spreadsheetTitle, setSpreadsheetTitle] = useState('')

  const canSync = Boolean(context)

  const sync = () => {
    if (!context) {
      pushToast({ kind: 'warning', message: 'Load contract context before syncing to Google Sheets.' })
      return
    }
    syncSheets.mutate(
      {
        context,
        spreadsheetId: spreadsheetId.trim(),
        spreadsheetTitle: spreadsheetTitle.trim() || `Contract Automation - ${context.client_name}`,
      },
      {
        onSuccess: result => {
          pushToast({
            kind: 'success',
            message: `Synced ${result.updated_rows} rows to Google Sheets.`,
          })
        },
        onError: error => pushToast({ kind: 'error', message: getErrorMessage(error) }),
      },
    )
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex items-start gap-3">
        <TableCellsIcon className="mt-0.5 h-5 w-5 text-gray-400" />
        <div>
          <h2 className="text-base font-semibold text-charcoal dark:text-gray-100">Google Sheets sync</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Push the populated contract context into a Drive spreadsheet for finance review.
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div>
          <Label htmlFor="contract-sheet-title">Spreadsheet title</Label>
          <Input
            id="contract-sheet-title"
            className="mt-1"
            value={spreadsheetTitle}
            onChange={event => setSpreadsheetTitle(event.target.value)}
            placeholder={context ? `Contract Automation - ${context.client_name}` : 'Contract Automation'}
            disabled={!canSync || syncSheets.isPending}
          />
        </div>
        <div>
          <Label htmlFor="contract-sheet-id">Existing spreadsheet ID</Label>
          <Input
            id="contract-sheet-id"
            className="mt-1"
            value={spreadsheetId}
            onChange={event => setSpreadsheetId(event.target.value)}
            placeholder="Leave blank to create a new spreadsheet"
            disabled={!canSync || syncSheets.isPending}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button variant="brand" onClick={sync} disabled={!canSync || syncSheets.isPending}>
          {syncSheets.isPending ? <Spinner size="sm" /> : <TableCellsIcon className="h-4 w-4" />}
          Sync to Google Sheets
        </Button>
        {syncSheets.data && (
          <a
            href={syncSheets.data.spreadsheet_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-eliza-red hover:underline"
          >
            Open spreadsheet
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
        )}
      </div>

      {!canSync && (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          Search for a client first, then sync the populated data.
        </p>
      )}
    </section>
  )
}
